import {Epic} from 'redux-observable'
import {concat, from, of} from 'rxjs'
import {
  catchError,
  filter,
  ignoreElements,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators'

import {RootState, AppActions} from 'app/rootReducer'
import Toast from 'components/Basic/Toast'
import {
  CACAssignments,
  CloudAppResponseType,
  CloudAppType,
  Policy,
} from 'interfaces'
import {i18n} from 'i18n'
import {PoliciesService} from 'services/api'
import Logger from 'services/Logger'
import StorageService, {
  POLICIES_STORAGE_NAMESPACE,
} from 'services/StorageService'
import {policiesActions} from '../policiesSlice'
import {CAC_ASSIGNMENT_ALL, normalizeCacData} from './cloudApp.utils'

const {t} = i18n

const CLOUD_APP_CACHE_KEY = 'getCloudApp'

const getCloudAppCacheKey = (policyName: string) =>
  `${CLOUD_APP_CACHE_KEY}-${encodeURIComponent(policyName)}`

type CachedCloudAppType = Omit<CloudAppType, 'assignments'> & {
  assignments?: CACAssignments
  values?: string[]
}

type CachedCloudAppResponse = Omit<CloudAppResponseType, 'cac'> & {
  cac?: Record<string, CachedCloudAppType>
}

// Pre-contraction Cloud App cache may still store gemini_allowed /
// copilot_allowed. Hydrating that snapshot and saving would serialize a
// missing restriction as Allow. Drop after 3 months.
type LegacyAdditionalControls = {
  gemini_allowed?: boolean
  copilot_allowed?: boolean
}

// Drop after 3 months: same pre-contraction cache guard as LegacyAdditionalControls.
const hasLegacyCacAllowedFlags = (cached: CachedCloudAppResponse): boolean =>
  Object.values(cached.cac ?? {}).some(app => {
    const controls = app.additional_controls as
      | LegacyAdditionalControls
      | undefined
    return (
      controls != null &&
      ('gemini_allowed' in controls || 'copilot_allowed' in controls)
    )
  })

const migrateCachedCloudApp = (
  cached: CachedCloudAppResponse,
): CloudAppResponseType => ({
  ...cached,
  cac: Object.fromEntries(
    Object.entries(cached.cac ?? {}).map(([appId, app]) => {
      const {values, assignments, ...currentApp} = app

      return [
        appId,
        {
          ...currentApp,
          assignments:
            assignments ??
            Object.fromEntries(
              (values ?? []).map(value => [value, CAC_ASSIGNMENT_ALL]),
            ),
        },
      ]
    }),
  ),
})

const logStorageError = (message: string, error: unknown) => {
  Logger.LogError(error as Error)
  Logger.LogError(
    new Error(`${message}: ${error instanceof Error ? error.message : error}`),
  )
}

const getCachedCloudApp = async (
  policyName: string,
): Promise<CloudAppResponseType | null> => {
  try {
    const cached = await StorageService.get<CachedCloudAppResponse>(
      getCloudAppCacheKey(policyName),
      POLICIES_STORAGE_NAMESPACE,
    )
    if (!cached) return null
    // Drop after 3 months: discard unmigrated extra-control caches.
    if (hasLegacyCacAllowedFlags(cached)) {
      await removeCloudAppCache(policyName)
      return null
    }

    return migrateCachedCloudApp(cached)
  } catch (error) {
    logStorageError('Error retrieving Cloud App cache', error)
    return null
  }
}

const saveCloudAppCache = async (
  policyName: string,
  data: CloudAppResponseType,
): Promise<void> => {
  try {
    await StorageService.set(
      getCloudAppCacheKey(policyName),
      data,
      POLICIES_STORAGE_NAMESPACE,
    )
  } catch (error) {
    logStorageError('Error saving Cloud App cache', error)
  }
}

const removeCloudAppCache = async (policyName: string): Promise<void> => {
  try {
    await StorageService.remove(
      getCloudAppCacheKey(policyName),
      POLICIES_STORAGE_NAMESPACE,
    )
  } catch (error) {
    logStorageError('Error removing Cloud App cache', error)
  }
}

export const getCloudAppEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.getCloudApp.match),
    switchMap(({payload}) =>
      from(getCachedCloudApp(payload.policyName)).pipe(
        switchMap(cachedData =>
          concat(
            of(
              ...(cachedData
                ? [policiesActions.getCloudAppSuccess(cachedData)]
                : []),
            ),
            from(PoliciesService.getCloudApp(payload)).pipe(
              mergeMap(({data}) =>
                from(saveCloudAppCache(payload.policyName, data)).pipe(
                  mergeMap(() => [policiesActions.getCloudAppSuccess(data)]),
                ),
              ),
              catchError((message: string) => {
                if (cachedData) {
                  return []
                }

                return concat(of(policiesActions.getCloudAppFailure(message)))
              }),
            ),
          ),
        ),
      ),
    ),
  )

const getCloudAppFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.getCloudAppFailure.match),
    tap(({payload: message}) => {
      Toast({message, type: 'error'})
    }),
    ignoreElements(),
  )

const getBaseCloudAppEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(policiesActions.getBasePolicyCloudApp.match),
    switchMap(() =>
      from(PoliciesService.getCloudApp({policyName: Policy.BasePolicy})).pipe(
        mergeMap(({data}) => [
          policiesActions.getBasePolicyCloudAppSuccess(data),
        ]),
        catchError((message: string) =>
          concat(of(policiesActions.getBasePolicyCloudAppFailure(message))),
        ),
      ),
    ),
  )

const getBaseCloudAppFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.getBasePolicyCloudAppFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

export const updateCloudAppEpic: Epic<AppActions, AppActions, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(policiesActions.updateCloudApp.match),
    switchMap(({payload}) => {
      const cloudAppData = state$.value.policies.cloudApp.data
      const cloudAppDataToCache = {
        ...cloudAppData,
        cac: normalizeCacData(cloudAppData.cac ?? {}),
      }

      return from(PoliciesService.updateCloudApp(payload)).pipe(
        mergeMap(() =>
          from(
            payload.cac === 'base'
              ? removeCloudAppCache(payload.policyName)
              : saveCloudAppCache(payload.policyName, cloudAppDataToCache),
          ).pipe(
            mergeMap(() => [
              policiesActions.updateCloudAppSuccess(
                t('policies:CLOUD_APP_UPDATED_SUCCESSFULLY'),
              ),
            ]),
          ),
        ),
        catchError((message: string) =>
          concat(of(policiesActions.updateCloudAppFailure(message))),
        ),
      )
    }),
  )

const updateCloudAppFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.updateCloudAppFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

const updateCloudAppSuccessEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.updateCloudAppSuccess.match),
    tap(({payload: message}) => Toast({message, type: 'success'})),
    ignoreElements(),
  )

export const cloudAppEpics = [
  getCloudAppEpic,
  getCloudAppFailureEpic,
  updateCloudAppEpic,
  updateCloudAppFailureEpic,
  updateCloudAppSuccessEpic,
  getBaseCloudAppEpic,
  getBaseCloudAppFailureEpic,
]
